import { useState, useEffect, useCallback } from 'react';
import { DatabaseService } from '../services/supabase';
import { Metric } from '../types/scorecard';

interface UseLiveEditReturn {
 isLoading: boolean;
 isSaving: boolean;
 error: string | null;
 lastSaved: Date | null;
 updateMetric: (metricId: string, field: 'value' | 'target', newValue: string) => Promise<void>;
 saveAll: () => Promise<void>;
 isOnline: boolean;
}

export const useLiveEdit = (
 initialData: any,
 onDataChange: (data: any) => void
): UseLiveEditReturn => {
 const [isLoading] = useState(false);
 const [isSaving, setIsSaving] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [lastSaved, setLastSaved] = useState<Date | null>(null);
 const [isOnline, setIsOnline] = useState(navigator.onLine);
 const [pendingUpdates, setPendingUpdates] = useState<Map<string, any>>(new Map());

 // Track online status
 useEffect(() => {
  const handleOnline = () => setIsOnline(true);
  const handleOffline = () => setIsOnline(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
   window.removeEventListener('online', handleOnline);
   window.removeEventListener('offline', handleOffline);
  };
 }, []);

 // Save all pending updates
 const saveAll = useCallback(async () => {
  console.log('useLiveEdit: saveAll called, pendingUpdates size:', pendingUpdates.size);
  if (pendingUpdates.size === 0) return;

  setIsSaving(true);
  setError(null);
  console.log('useLiveEdit: Starting save process');

  try {
   // Capture the updates we are about to save so we can clear them afterwards
   const updatesToSave = new Map(pendingUpdates);
   console.log('useLiveEdit: Updates to save:', updatesToSave);
   
   // Save individual metric updates
   for (const [metricId, updates] of Array.from(updatesToSave.entries())) {
    console.log('useLiveEdit: Saving metric', metricId, updates);
    await DatabaseService.updateMetric(
     metricId, 
     updates.value || '', 
     updates.target || ''
    );
   }

   // Save complete scorecard data
   console.log('useLiveEdit: Saving complete scorecard data');
   await DatabaseService.saveScorecardData(initialData);

   // Remove the items we just saved from pendingUpdates (in case new ones were added while awaiting)
   setPendingUpdates(prev => {
    const remaining = new Map(prev);
    for (const key of Array.from(updatesToSave.keys())) {
     remaining.delete(key);
    }
    return remaining;
   });
   
   const savedTime = new Date();
   setLastSaved(savedTime);
   console.log('useLiveEdit: Save completed successfully at', savedTime);

  } catch (err) {
   console.error('useLiveEdit: Save failed', err);
   setError(err instanceof Error ? err.message : 'Failed to save changes');
  } finally {
   setIsSaving(false);
  }
 }, [pendingUpdates, initialData]);

 // Auto-save with debouncing using useEffect
 useEffect(() => {
  if (pendingUpdates.size > 0 && isOnline) {
   const timeout = setTimeout(() => {
    saveAll();
   }, 2000);
   return () => clearTimeout(timeout);
  }
 }, [pendingUpdates, isOnline, saveAll]);

 // Update metric with auto-save
 const updateMetric = async (metricId: string, field: 'value' | 'target', newValue: string) => {
  try {
   setError(null);
   
   // Update local state immediately for responsiveness
   setPendingUpdates(prev => {
    const updated = new Map(prev);
    const existing = updated.get(metricId) || {};
    updated.set(metricId, { ...existing, [field]: newValue });
    return updated;
   });

   // Apply change to local data
   const updatedData = updateMetricInData(initialData, metricId, field, newValue);
   onDataChange(updatedData);

  } catch (err) {
   setError(err instanceof Error ? err.message : 'Failed to update metric');
  }
 };

 // Helper function to update metric in data structure
 const updateMetricInData = (data: any, metricId: string, field: string, value: string): any => {
  const updateMetricRecursive = (metrics: Metric[]): Metric[] => {
   return metrics.map(metric => {
    if (metric.id === metricId) {
     return {
      ...metric,
      [field]: value,
      lastUpdated: new Date()
     };
    }
    if (metric.subMetrics && metric.subMetrics.length > 0) {
     return {
      ...metric,
      subMetrics: updateMetricRecursive(metric.subMetrics)
     };
    }
    return metric;
   });
  };

  return {
   ...data,
   currentVersion: data.currentVersion.map((category: any) => ({
    ...category,
    levels: category.levels.map((level: any) => ({
     ...level,
     metrics: updateMetricRecursive(level.metrics)
    }))
   })),
   lastModified: new Date()
  };
 };

 return {
  isLoading,
  isSaving,
  error,
  lastSaved,
  updateMetric,
  saveAll,
  isOnline
 };
};